// Lead meetings: list / create / cancel. Endpoint /leads/:leadId/meetings.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";

const key = (leadId) => ["crm", "lead-meetings", leadId];

// Meeting CUD touches more than the per-lead meetings list. The dashboard
// "Scheduled meetings" widget and the cross-lead Activities feed both surface
// meetings, so refresh them too. Per-lead activities was already handled.
function invalidateMeeting(qc, leadId) {
  qc.invalidateQueries({ queryKey: key(leadId) });
  qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
  qc.invalidateQueries({ queryKey: ["crm", "activities", "feed"] });
  qc.invalidateQueries({ queryKey: ["crm", "dashboard", "overview"] });
}

// A meeting is "in flight" when the backend is doing async work whose result
// only surfaces on the next fetch — Kimi summarising, Vexa bot recording,
// transcript still pending. We poll while any such meeting exists so the UI
// flips from "generating…" to the final summary without the user refreshing.
// A summary is only "in flight" for a BOUNDED window after its transcript was
// set — otherwise a meeting whose summary silently failed/never ran would make
// us poll every 5s forever (and show "generating…" forever). After the window,
// an unsummarised transcript is "stuck", not "in progress".
const SUMMARY_WINDOW_MS = 2 * 60 * 1000;
function awaitingSummary(transcript, ai, transcriptUpdatedAt) {
  if (!transcript || ai?.summary || ai?.error) return false;
  const ts = transcriptUpdatedAt ? new Date(transcriptUpdatedAt).getTime() : 0;
  return ts > 0 && (Date.now() - ts) < SUMMARY_WINDOW_MS;
}

function hasInFlightWork(meetings) {
  if (!Array.isArray(meetings)) return false;
  return meetings.some((m) => {
    if (m.status === "canceled") return false;
    // Recurring meeting: in-flight if ANY session is recording or has a
    // transcript whose summary is still within the generating window.
    if (m.isRecurring && Array.isArray(m.sessions)) {
      return m.sessions.some((s) => {
        if (s.status === "recording") return true;
        return awaitingSummary(s.transcript, s.aiSummary, s.transcriptUpdatedAt);
      });
    }
    // Transcript present and summary plausibly still generating.
    if (awaitingSummary(m.transcript, m.aiSummary, m.transcriptUpdatedAt)) return true;
    // Vexa bot is actively recording/joining — transcript pending.
    const botStatus = m.vexaBot?.status;
    if (botStatus === "joining" || botStatus === "recording" || botStatus === "requested") {
      return true;
    }
    return false;
  });
}

export function useLeadMeetings(leadId) {
  return useQuery({
    queryKey: key(leadId),
    enabled: !!leadId,
    queryFn: async () => {
      const { data } = await crmApi.get(`/leads/${leadId}/meetings`);
      return data?.meetings || [];
    },
    // Auto-refresh every 5s while async work is in flight. Returns false
    // once all meetings are settled — react-query then stops polling on its
    // own. No tab-focus polling needed since we already invalidate on user
    // actions (sync, transcript save, etc.).
    refetchInterval: (query) => (hasInFlightWork(query.state.data) ? 5000 : false),
    refetchIntervalInBackground: false,
  });
}

export function useCreateLeadMeeting(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await crmApi.post(`/leads/${leadId}/meetings`, payload)).data,
    onSuccess: () => invalidateMeeting(qc, leadId),
  });
}

export function useCancelLeadMeeting(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (meetingId) => (await crmApi.delete(`/leads/${leadId}/meetings/${meetingId}`)).data,
    onSuccess: () => invalidateMeeting(qc, leadId),
  });
}

// Attach (or overwrite) a meeting's transcript. Server triggers Kimi
// summarization in the background; poll the meetings list to see the result.
export function useSetMeetingTranscript(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ meetingId, transcript }) =>
      (await crmApi.post(`/leads/${leadId}/meetings/${meetingId}/transcript`, { transcript })).data,
    onSuccess: () => invalidateMeeting(qc, leadId),
  });
}

// Dispatch the Vexa bot to this meeting's Meet link right now. User triggers
// this from the meeting card before / during the call.
export function useStartMeetingBot(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (meetingId) =>
      (await crmApi.post(`/leads/${leadId}/meetings/${meetingId}/start-bot`)).data,
    onSuccess: () => invalidateMeeting(qc, leadId),
  });
}

// Pull transcript/recording from Vexa.
//   mutate(meetingId)                   → background pull (auto-sync); never
//                                         touches a live bot.
//   mutate({ meetingId, manual: true }) → user clicked Sync; also pulls the
//                                         bot out of the call to finalise it.
export function usePullVexaTranscript(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      const meetingId = typeof arg === "string" ? arg : arg.meetingId;
      const manual = typeof arg === "object" && arg.manual;
      const qs = manual ? "?manual=1" : "";
      return (await crmApi.post(`/leads/${leadId}/meetings/${meetingId}/pull-vexa${qs}`)).data;
    },
    onSuccess: () => invalidateMeeting(qc, leadId),
  });
}

// Ask Google Meet whether a meeting has ended. Returns { ended, endTime?,
// reason? }. The Sync button only shows once this reports ended: true.
export function useCheckMeetingEnded(leadId) {
  return useMutation({
    mutationFn: async (meetingId) =>
      (await crmApi.get(`/leads/${leadId}/meetings/${meetingId}/ended-status`)).data,
  });
}

// Pull recording + transcript from Google Drive for this meeting. Response:
// { meeting, synced: { recording, transcript }, reason? }. Server kicks off
// Kimi summary in background if a transcript was fetched.
export function useSyncMeeting(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (meetingId) =>
      (await crmApi.post(`/leads/${leadId}/meetings/${meetingId}/sync`)).data,
    onSuccess: () => invalidateMeeting(qc, leadId),
  });
}

// Force-regenerate the cached aiSummary for a single meeting. Mirrors the
// "Re-summarize" button on calls. Synchronous: the response carries the new
// aiSummary so we can drop it into cache without polling.
export function useRegenerateMeetingSummary(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (meetingId) =>
      (await crmApi.post(`/leads/${leadId}/meetings/${meetingId}/regenerate-summary`)).data,
    onSuccess: () => invalidateMeeting(qc, leadId),
  });
}

// GET cached rollup across all meetings for the lead. Returns whatever's
// stored on the Lead doc (null if never generated). Companion to the POST
// hook below, which regenerates and re-hydrates this cache.
export function useLeadMeetingsRollup(leadId) {
  return useQuery({
    queryKey: ["crm", "meetings", leadId, "rollup"],
    enabled: !!leadId,
    queryFn: async () =>
      (await crmApi.get(`/leads/${leadId}/meetings/rollup-summary`)).data,
    staleTime: 60_000,
  });
}

// Master rollup summary across every meeting on this lead — parallel to
// useGenerateLeadCallsRollup. Server persists the result onto the Lead doc
// (meetingsRollupSummary), so re-opening the panel surfaces the cached
// rollup without re-running Kimi.
export function useGenerateLeadMeetingsRollup(leadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await crmApi.post(`/leads/${leadId}/meetings/rollup-summary`)).data,
    onSuccess: (data) => {
      // Hydrate the cache so the panel updates immediately — same pattern as
      // useGenerateLeadCallsRollup.
      qc.setQueryData(["crm", "meetings", leadId, "rollup"], {
        ok: true,
        summary: data.summary,
        generatedAt: data.generatedAt,
        meetingsIncluded: data.meetingsIncluded,
        error: null,
      });
    },
  });
}
